import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * UserSignalFlowPage
 * -------------------
 * Son N scanner sonucunun pipeline akışını gösterir:
 *   1) Scanner Candidate (LG/ST filter)
 *   2) Signal Engine (4 strateji + aggregate rule)
 *   3) Bot Trader Gate (Fibonacci + R:R + sizing)
 *   4) Dispatch
 * Her satır bir sembol; node'lar renklerle durumu gösterir.
 *
 * Kullanım: GET /api/user/scanner/results?limit=100
 */

const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { hour12: false });
  } catch {
    return String(iso);
  }
};

const Node = ({ label, status, tone, testId, subtitle }) => {
  const toneMap = {
    emerald: "bg-emerald-500/25 text-emerald-200 border-emerald-500/50",
    rose: "bg-rose-500/25 text-rose-200 border-rose-500/50",
    amber: "bg-amber-500/25 text-amber-200 border-amber-500/50",
    cyan: "bg-cyan-500/25 text-cyan-200 border-cyan-500/50",
    slate: "bg-slate-700/30 text-slate-400 border-slate-600/40",
  };
  return (
    <div
      className={`flex min-w-[130px] flex-col items-center rounded border px-2 py-1.5 text-center ${toneMap[tone] || toneMap.slate}`}
      data-testid={testId}
    >
      <p className="text-[9px] font-semibold uppercase tracking-widest">{label}</p>
      <p className="mt-0.5 text-[11px] font-bold">{status}</p>
      {subtitle ? <p className="mt-0.5 text-[9px] opacity-70">{subtitle}</p> : null}
    </div>
  );
};

const Arrow = () => (
  <div className="flex items-center" aria-hidden>
    <svg width="22" height="10" viewBox="0 0 22 10" className="text-slate-500">
      <path d="M0 5 L18 5 M14 1 L18 5 L14 9" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  </div>
);

const deriveStages = (row) => {
  const pl = row?.payload || {};
  const side = String(row?.signal || "none").toLowerCase();

  // 1) Scanner Candidate stage
  const isLongCandidate = Boolean(pl.scanner_long_candidate);
  const isShortCandidate = Boolean(pl.scanner_short_candidate);
  const isCandidate = isLongCandidate || isShortCandidate;
  const sonlg = pl.sonlg || 0;
  const sonst = pl.sonst || 0;
  let candidateLabel = "NO";
  let candidateTone = "slate";
  if (isLongCandidate) {
    candidateLabel = `LONG (SONLG=${sonlg})`;
    candidateTone = "emerald";
  } else if (isShortCandidate) {
    candidateLabel = `SHORT (SONST=${sonst})`;
    candidateTone = "rose";
  }

  // 2) Signal Engine stage
  const longApprovals = Array.isArray(pl.long_approvals) ? pl.long_approvals : [];
  const shortApprovals = Array.isArray(pl.short_approvals) ? pl.short_approvals : [];
  const engineApprovalCount =
    side === "long" ? longApprovals.length : side === "short" ? shortApprovals.length : 0;
  let engineLabel = "NONE";
  let engineTone = "slate";
  if (side === "long") {
    engineLabel = `LONG ${engineApprovalCount}/4`;
    engineTone = engineApprovalCount >= 2 ? "emerald" : "amber";
  } else if (side === "short") {
    engineLabel = `SHORT ${engineApprovalCount}/4`;
    engineTone = engineApprovalCount >= 2 ? "rose" : "amber";
  }

  // 3) Aggregate rule / Send-to-Bot
  const sendToBot = Boolean(pl.send_to_bot);
  const hasTrigger =
    side === "long"
      ? Boolean(pl.long_has_entry_trigger)
      : side === "short"
      ? Boolean(pl.short_has_entry_trigger)
      : false;
  let aggLabel = "FAIL";
  let aggTone = "rose";
  if (sendToBot) {
    aggLabel = "SEND TO BOT";
    aggTone = "emerald";
  } else if (engineApprovalCount >= 2 && !hasTrigger) {
    aggLabel = "NO TRIGGER";
    aggTone = "amber";
  } else if (engineApprovalCount === 1) {
    aggLabel = "COUNT < 2";
    aggTone = "amber";
  } else if (engineApprovalCount === 0) {
    aggLabel = "NO APPROVAL";
    aggTone = "slate";
  }

  // 4) Bot Trader Gate (Fibonacci + R:R)
  const fib = pl.fibonacci_plan || {};
  const fibValid = Boolean(fib?.valid);
  const rr = fib?.risk_reward;
  let gateLabel = "—";
  let gateTone = "slate";
  let gateSub = null;
  if (sendToBot && fibValid) {
    gateLabel = "GATE PASS";
    gateTone = "emerald";
    gateSub = `R:R ${Number(rr || 0).toFixed(2)}`;
  } else if (sendToBot && !fibValid) {
    const reason = String(fib?.reason || "").replace(/_/g, " ");
    gateLabel = "GATE FAIL";
    gateTone = "amber";
    gateSub = reason.slice(0, 24) || "plan invalid";
  }

  // 5) Dispatch — 2026-06-11 P1: gerçek execution status (backend enrichment).
  // execution_status alanı yoksa eski türetilmiş davranışa düş.
  const execStatus = String(row?.execution_status || "").toUpperCase();
  const execReason = row?.execution_reason || null;
  const execIntentId = row?.execution_intent_id || null;
  const execPositionId = row?.execution_position_id || null;
  let dispatchLabel = "—";
  let dispatchTone = "slate";
  let dispatchSub = null;
  if (execStatus === "POSITION_OPENED") {
    dispatchLabel = "POSITION OPENED";
    dispatchTone = "emerald";
    dispatchSub = execPositionId ? `pos: ${String(execPositionId).slice(0, 8)}` : null;
  } else if (execStatus === "DISPATCHED") {
    dispatchLabel = "DISPATCHED";
    dispatchTone = "cyan";
    dispatchSub = execIntentId ? `intent: ${String(execIntentId).slice(0, 8)}` : null;
  } else if (execStatus === "BLOCKED") {
    dispatchLabel = "BLOCKED";
    dispatchTone = "amber";
    dispatchSub = execReason ? String(execReason).slice(0, 22) : "blocked";
  } else if (execStatus === "REJECTED") {
    dispatchLabel = "REJECTED";
    dispatchTone = "rose";
    dispatchSub = execReason ? String(execReason).slice(0, 22) : "rejected";
  } else if (execStatus === "FAILED") {
    dispatchLabel = "FAILED";
    dispatchTone = "rose";
    dispatchSub = execReason ? String(execReason).slice(0, 22) : "failed";
  } else if (execStatus === "READY") {
    dispatchLabel = "READY";
    dispatchTone = "amber";
    dispatchSub = "manuel onay bekliyor";
  } else if (execStatus === "PENDING") {
    dispatchLabel = "PENDING";
    dispatchTone = "amber";
    dispatchSub = execReason ? String(execReason).slice(0, 22) : null;
  } else if (sendToBot && fibValid && side !== "none" && hasTrigger) {
    // Backend enrichment yoksa eski türetilmiş davranış (deploy geçici fallback).
    // 2026-02-13 v22 — Plan B+ Faz 2.5 (G4): Yanlış "DISPATCHED*" render hatası fix.
    // Fallback YALNIZCA side != none VE hasTrigger=true VE sendToBot=true iken
    // gösterilir. SIGNAL_ENGINE_NONE veya trigger yokken DISPATCHED gözükmesin.
    dispatchLabel = "DISPATCHED*";
    dispatchTone = "cyan";
    dispatchSub = "no exec data";
  } else if (side === "none" || engineApprovalCount === 0) {
    // 2026-02-13 v22 — Plan B+ Faz 2.5 (G4): SIGNAL_ENGINE_NONE → AWAITING_TRIGGER badge.
    dispatchLabel = "AWAITING_TRIGGER";
    dispatchTone = "slate";
    dispatchSub = "engine NONE";
  } else if (!hasTrigger && engineApprovalCount >= 2) {
    // Approval var ama trigger yok → dispatch_validator NO_ENTRY_TRIGGER ile reject.
    dispatchLabel = "REJECTED";
    dispatchTone = "rose";
    dispatchSub = "no_entry_trigger";
  } else if (!sendToBot && engineApprovalCount >= 1 && engineApprovalCount < 2) {
    // Approval sayısı yetersiz → WAIT.
    dispatchLabel = "WAIT";
    dispatchTone = "amber";
    dispatchSub = "insufficient_approvals";
  }

  return {
    candidate: { label: candidateLabel, tone: candidateTone, subtitle: isCandidate ? "filter geçti" : null },
    engine: {
      label: engineLabel,
      tone: engineTone,
      subtitle:
        side === "long"
          ? longApprovals.slice(0, 2).join(", ")
          : side === "short"
          ? shortApprovals.slice(0, 2).join(", ")
          : null,
    },
    aggregate: { label: aggLabel, tone: aggTone, subtitle: hasTrigger ? "trigger ✓" : "no trigger" },
    gate: { label: gateLabel, tone: gateTone, subtitle: gateSub },
    dispatch: { label: dispatchLabel, tone: dispatchTone, subtitle: dispatchSub },
  };
};

/**
 * 2026-06-11 P1: Duplicate sinyallerin UI'da tekrar tekrar görünmesini engeller.
 * Aynı (symbol + side + run_id) için aynı dakikada gelen kayıtlar gruplanır;
 * yalnızca en güncel (oldest = en yeni `generated_at`) tutulur ve `dupe_count`
 * alanı ile "x3" gibi UI rozeti üretilebilir.
 */
const dedupeRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const buckets = new Map();
  for (const row of rows) {
    const symbol = String(row?.symbol || "").toUpperCase();
    const side = String(row?.signal || "none").toLowerCase();
    const runId = String(row?.run_id || "").slice(0, 24);
    // Aynı run_id yoksa generated_at dakika bazlı bucket'a göre grupla
    const minuteBucket = row?.generated_at ? String(row.generated_at).slice(0, 16) : "";
    const key = `${symbol}|${side}|${runId || minuteBucket}`;
    if (!buckets.has(key)) {
      buckets.set(key, { ...row, dupe_count: 1, dupe_ids: [row.id] });
    } else {
      const existing = buckets.get(key);
      existing.dupe_count += 1;
      existing.dupe_ids.push(row.id);
      // En yeni generated_at'i sakla
      if (row?.generated_at && (!existing.generated_at || row.generated_at > existing.generated_at)) {
        existing.generated_at = row.generated_at;
        // payload da en yeniyi koru (execution_status da en güncel olur)
        if (row.payload) existing.payload = row.payload;
        if (row.execution_status) existing.execution_status = row.execution_status;
        if (row.execution_reason) existing.execution_reason = row.execution_reason;
        if (row.execution_intent_id) existing.execution_intent_id = row.execution_intent_id;
        if (row.execution_position_id) existing.execution_position_id = row.execution_position_id;
      }
    }
  }
  // En yeni → en eski sıralaması
  return Array.from(buckets.values()).sort((a, b) => {
    const ta = a.generated_at || "";
    const tb = b.generated_at || "";
    return tb.localeCompare(ta);
  });
};

export const UserSignalFlowPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiClient.get("/user/scanner/results", { params: { limit: 100 }, timeout: 15000 });
        if (cancelled) return;
        setRows(Array.isArray(resp?.data) ? resp.data : []);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const { summary, filteredRows } = useMemo(() => {
    // 2026-06-11 P1: Aynı (symbol+side+run_id) için duplicate satırları grupla.
    const dedupedRows = dedupeRows(rows);
    const stats = {
      total: dedupedRows.length,
      candidates: 0,
      engine_onay: 0,
      send_to_bot: 0,
      dispatched: 0,
      rejected: 0,
      position_opened: 0,
      blocked: 0,
    };
    dedupedRows.forEach((row) => {
      const pl = row?.payload || {};
      if (pl.scanner_long_candidate || pl.scanner_short_candidate) stats.candidates += 1;
      const side = String(row?.signal || "").toLowerCase();
      const aCount =
        side === "long"
          ? (pl.long_approvals || []).length
          : side === "short"
          ? (pl.short_approvals || []).length
          : 0;
      if (aCount >= 1) stats.engine_onay += 1;
      if (pl.send_to_bot) stats.send_to_bot += 1;
      const execStatus = String(row?.execution_status || "").toUpperCase();
      if (execStatus === "POSITION_OPENED") {
        stats.position_opened += 1;
        stats.dispatched += 1;
      } else if (execStatus === "DISPATCHED") {
        stats.dispatched += 1;
      } else if (execStatus === "BLOCKED") {
        stats.blocked += 1;
        stats.rejected += 1;
      } else if (execStatus === "REJECTED" || execStatus === "FAILED") {
        stats.rejected += 1;
      } else {
        // execution_status enrichment olmadığında eski heuristic
        const fib = pl.fibonacci_plan || {};
        if (pl.send_to_bot && fib?.valid) stats.dispatched += 1;
        else if (aCount >= 1 && !pl.send_to_bot) stats.rejected += 1;
      }
    });
    const fr = dedupedRows.filter((row) => {
      const pl = row?.payload || {};
      if (filter === "all") return true;
      const execStatus = String(row?.execution_status || "").toUpperCase();
      if (filter === "dispatched") {
        if (execStatus === "POSITION_OPENED" || execStatus === "DISPATCHED") return true;
        return pl.send_to_bot && pl?.fibonacci_plan?.valid;
      }
      if (filter === "position_opened") return execStatus === "POSITION_OPENED";
      if (filter === "blocked") return execStatus === "BLOCKED" || execStatus === "REJECTED" || execStatus === "FAILED";
      if (filter === "rejected") {
        const side = String(row?.signal || "").toLowerCase();
        const aCount =
          side === "long"
            ? (pl.long_approvals || []).length
            : side === "short"
            ? (pl.short_approvals || []).length
            : 0;
        return aCount >= 1 && !pl.send_to_bot;
      }
      if (filter === "candidate_only") {
        const isCand = pl.scanner_long_candidate || pl.scanner_short_candidate;
        const side = String(row?.signal || "").toLowerCase();
        const aCount =
          side === "long"
            ? (pl.long_approvals || []).length
            : side === "short"
            ? (pl.short_approvals || []).length
            : 0;
        return isCand && aCount === 0;
      }
      return true;
    });
    return { summary: stats, filteredRows: fr };
  }, [rows, filter]);

  return (
    <section className="mx-auto max-w-7xl space-y-4 p-4 text-slate-100" data-testid="user-signal-flow-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold" data-testid="signal-flow-title">Signal Flow — Akış Geçmişi</h1>
          <p className="text-xs text-slate-400">
            Scanner → Signal Engine → Aggregate Rule → Bot Trader Gate → Dispatch
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setRefreshToken((v) => v + 1)}
          data-testid="signal-flow-refresh-button"
          disabled={loading}
        >
          {loading ? "Yükleniyor..." : "Yenile"}
        </Button>
      </header>

      <div className="grid gap-2 md:grid-cols-6" data-testid="signal-flow-summary">
        <div className="rounded border border-slate-800 bg-slate-900 p-2" data-testid="signal-flow-summary-total">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Toplam</p>
          <p className="text-lg font-bold">{summary.total}</p>
        </div>
        <div className="rounded border border-cyan-800 bg-cyan-950/40 p-2" data-testid="signal-flow-summary-candidates">
          <p className="text-[10px] uppercase tracking-wider text-cyan-300">Scanner Aday</p>
          <p className="text-lg font-bold text-cyan-200">{summary.candidates}</p>
        </div>
        <div className="rounded border border-violet-800 bg-violet-950/40 p-2" data-testid="signal-flow-summary-engine">
          <p className="text-[10px] uppercase tracking-wider text-violet-300">Signal Onay</p>
          <p className="text-lg font-bold text-violet-200">{summary.engine_onay}</p>
        </div>
        <div className="rounded border border-amber-800 bg-amber-950/40 p-2" data-testid="signal-flow-summary-blocked">
          <p className="text-[10px] uppercase tracking-wider text-amber-300">Blocked / Failed</p>
          <p className="text-lg font-bold text-amber-200">{summary.blocked + (summary.rejected - summary.blocked)}</p>
        </div>
        <div className="rounded border border-cyan-800 bg-cyan-950/40 p-2" data-testid="signal-flow-summary-dispatched">
          <p className="text-[10px] uppercase tracking-wider text-cyan-300">Dispatched</p>
          <p className="text-lg font-bold text-cyan-200">{summary.dispatched}</p>
        </div>
        <div className="rounded border border-emerald-800 bg-emerald-950/40 p-2" data-testid="signal-flow-summary-position-opened">
          <p className="text-[10px] uppercase tracking-wider text-emerald-300">Position Opened</p>
          <p className="text-lg font-bold text-emerald-200">{summary.position_opened}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1" data-testid="signal-flow-filters">
        {[
          { key: "all", label: "Tümü" },
          { key: "position_opened", label: "Position Opened" },
          { key: "dispatched", label: "Dispatched" },
          { key: "blocked", label: "Blocked / Failed" },
          { key: "rejected", label: "Reject" },
          { key: "candidate_only", label: "Sadece Aday" },
        ].map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}
            className="text-xs"
            data-testid={`signal-flow-filter-${f.key}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {error ? (
        <div className="rounded border border-rose-700 bg-rose-950/40 p-3 text-xs text-rose-300" data-testid="signal-flow-error">
          Hata: {error}
        </div>
      ) : null}

      {filteredRows.length === 0 && !loading ? (
        <div className="rounded border border-slate-800 bg-slate-900 p-6 text-center text-xs text-slate-500" data-testid="signal-flow-empty">
          Bu filtrede sonuç yok. Scanner&apos;ı çalıştırmayı deneyin.
        </div>
      ) : null}

      <div className="space-y-2" data-testid="signal-flow-timeline">
        {filteredRows.map((row) => {
          const stages = deriveStages(row);
          return (
            <article
              key={row.id}
              className="rounded border border-slate-800 bg-slate-900 p-3"
              data-testid={`signal-flow-row-${row.id}`}
            >
              <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="text-sm font-semibold" data-testid={`signal-flow-row-symbol-${row.id}`}>
                    {row.symbol}
                  </span>
                  {row.dupe_count > 1 ? (
                    <span
                      className="ml-2 inline-flex items-center rounded bg-violet-600/30 px-1.5 py-0.5 text-[10px] font-bold text-violet-200"
                      data-testid={`signal-flow-row-dupe-${row.id}`}
                      title="Aynı sembol+yön+run için kaç kayıt birleştirildi"
                    >
                      x{row.dupe_count}
                    </span>
                  ) : null}
                  <span className="ml-2 text-[10px] text-slate-500" data-testid={`signal-flow-row-time-${row.id}`}>
                    {formatTime(row?.generated_at)}
                  </span>
                </div>
                <div className="flex gap-2 text-[10px] text-slate-400">
                  <span data-testid={`signal-flow-row-strategy-${row.id}`}>strategy: {row?.strategy_code || "—"}</span>
                  <span data-testid={`signal-flow-row-run-${row.id}`}>run: {String(row?.run_id || "").slice(0, 8)}</span>
                </div>
              </header>
              <div className="flex flex-wrap items-center gap-1">
                <Node
                  label="1. Scanner (1h)"
                  status={stages.candidate.label}
                  tone={stages.candidate.tone}
                  subtitle={stages.candidate.subtitle}
                  testId={`signal-flow-row-node-candidate-${row.id}`}
                />
                <Arrow />
                <Node
                  label="2. Signal Engine (15m)"
                  status={stages.engine.label}
                  tone={stages.engine.tone}
                  subtitle={stages.engine.subtitle}
                  testId={`signal-flow-row-node-engine-${row.id}`}
                />
                <Arrow />
                <Node
                  label="3. Aggregate Rule"
                  status={stages.aggregate.label}
                  tone={stages.aggregate.tone}
                  subtitle={stages.aggregate.subtitle}
                  testId={`signal-flow-row-node-aggregate-${row.id}`}
                />
                <Arrow />
                <Node
                  label="4. Bot Gate"
                  status={stages.gate.label}
                  tone={stages.gate.tone}
                  subtitle={stages.gate.subtitle}
                  testId={`signal-flow-row-node-gate-${row.id}`}
                />
                <Arrow />
                <Node
                  label="5. Dispatch"
                  status={stages.dispatch.label}
                  tone={stages.dispatch.tone}
                  subtitle={stages.dispatch.subtitle}
                  testId={`signal-flow-row-node-dispatch-${row.id}`}
                />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default UserSignalFlowPage;
